<template>
    <main>
        <header>
            <h1 v-if="isUserInfo">会員情報変更</h1>
            <h1 v-if="isFavoriteClub">お気に入りクラブ・メール設定</h1>
            <h1 v-if="isOneTouchPass && !isAddOneTouchPass">登録済みワンタッチパスID</h1>
            <h1 v-if="isOneTouchPass && isAddOneTouchPass">ワンタッチパスID情報登録</h1>
            <h1 v-if="isEmailSetting">メール設定</h1>
        </header>

        <slot name="errors"></slot>
        <div v-if="showLoading" class="jleague_loader">

        </div>
        <div v-else>
            <template v-if="!isChanged">
                <password-confirm v-if="isPasswordPage"
                                  :data-csrf-name="dataCsrfName"
                                  :data-csrf="dataCsrf"
                                  :params="urlParams"
                >
                    <template v-slot:note>
                        <p>登録した会員情報を変更するにあたり、ＪリーグID登録時に設定したパスワードを入力してください。</p>
                    </template>

                    <template v-slot:recovery-note>
                        <slot name="recovery-request"></slot>
                    </template>
                </password-confirm>
                <template v-else>
                    <user-info v-if="isUserInfo"
                               :data-csrf-name="dataCsrfName"
                               :data-csrf="dataCsrf"
                               :data-token="dataToken"
                               :userinfo="userinfo"
                               @alert-message="alertMessage"
                    ></user-info>

                    <favorite-club v-if="isFavoriteClub"
                                   :data-csrf-name="dataCsrfName"
                                   :data-csrf="dataCsrf"
                                   :data-token="dataToken"
                                   :userinfo="userinfo"
                                   @alert-message="alertMessage"
                    ></favorite-club>

                    <one-touch v-if="isOneTouchPass"
                               :data-csrf-name="dataCsrfName"
                               :data-csrf="dataCsrf"
                               :data-token="dataToken"
                               :userinfo="userinfo"
                               @add-one-touch-pass="addOneTouchPass"
                    >
                        <template v-slot:note>
                            <p>ワンタッチパス対象となっているクラブ会員情報を登録することでJ リーグチケットなどファンクラブ割引などのサービスをご利用いただくことが可能です。</p>
                            <p>お手持ちのワンタッチパスID（クラブ発行会員番号）およびパスワードを入力し、ワンタッチパス対象の会員情報を登録してください。</p>
                            <p><a class="linkMark" target="_blank" href="https://www.jleague-ticket.jp/guide/ticket_limited">J リーグチケットでの会員限定発売について</a></p>
                        </template>
                    </one-touch>

                    <email-setting v-if="isEmailSetting"
                                   :data-csrf-name="dataCsrfName"
                                   :data-csrf="dataCsrf"
                                   :data-token="dataToken"
                                   :userinfo="userinfo"
                    ></email-setting>
                </template>
            </template>
            <div v-else class="regist">
                <div class="msg">
                    <p v-if="isUserInfo">
                        会員情報の変更が完了しました。
                    </p>
                    <p v-if="isFavoriteClub">
                        お気に入りクラブ・メール設定<br>
                        が完了しました。
                    </p>
                    <p v-if="isOneTouchPass">
                        ワンタッチパスIDの解除が完了しました。
                    </p>
                    <p v-if="isEmailSetting">
                        メール設定の変更<br>
                        が完了しました。
                    </p>
                </div>
                <slot name="back-service-button"></slot>
            </div>
        </div>

    </main>
</template>

<script>
import Vue from 'vue'
import Toasted from 'vue-toasted'
import { mixinJleague, EnumEditPage } from '../mixin/utility'
import PasswordConfirm from './PasswordConfirm'
import UserInfo from './UserInfo'
import FavoriteClub from './FavoriteClub'
import OneTouch from './OneTouch'
import EmailSetting from './EmailSetting'

const EDIT_USERINFO_PARAM_KEY = 'edit'

// ステージングで thymeleaf のファイルが完全なHTMLになったとき、
// 何故かJSがコケてしまうため `window.addEventListener` で load したとき `Vue.use` する。
// see. https://www.milldea.com/redmine/issues/19270#note-2
// see. https://stackoverflow.com/questions/57180011/i-can-not-install-vue-toasted-by-vue-use-2019
window.addEventListener('load', () => {
    Vue.use(Toasted, {
        action: {
            icon: 'fa-times-circle',
            onClick: function(event, toastObject){
                toastObject.goAway(0);
            }
        },
        position: 'top-center',
        duration: 5000,
        fullWidth: true,
        fitToScreen: true,
        iconPack: 'fontawesome',
        className: 'toasted-jleague'
    })
});

export default {
    name: 'ChangeUserinfo',
    components: {
        'password-confirm': PasswordConfirm,
        'user-info': UserInfo,
        'favorite-club': FavoriteClub,
        'one-touch': OneTouch,
        'email-setting': EmailSetting,
    },
    mixins: [mixinJleague],
    data() {
        return {
            isAddOneTouchPass: false,
            showLoading: false
        }
    },
    props: {
        dataCsrf: {
            type: String,
            required: true
        },
        dataCsrfName: {
            type: String,
            required: true
        },
        dataToken: {
            type: String,
            required: true
        },
        dataChanged: {
            type: String,
            required: true,
            default: '',    // thymeleaf ${changed} の値が false のとき prop にレンダリングされないため
        },
        dataPasswordPage: {
            type: String,
            required: true,
            default: '',    // thymeleaf ${passwordPage} の値が false のとき prop にレンダリングされないため
        },
        dataUserInfoJson: {
            type: String,
            required: true,
        },
        dataParamEdit: {
            type: String,
            required: true,
            default: '',    // thymeleaf ${paramEdit} の値がないとき prop にレンダリングされないため
        },
    },
    computed: {
        userinfo: function(){
            let userInfoJson;
            try {
                userInfoJson = JSON.parse(this.dataUserInfoJson)
            } catch (e) {
                // `strUserInfoJson` の文字列が有効な JSON でない場合
                // エラー画面に遷移してメッセージを表示する
                location.href = '/error?path=change-userinfo';
            }

            return userInfoJson;
        },
        referrer: function(){
            return document.referrer;
        },
        isUserInfo: function(){
            return this.urlParams.edit === EnumEditPage.UserInfo;
        },
        isFavoriteClub: function(){
            return this.urlParams.edit === EnumEditPage.FavoriteClub;
        },
        isOneTouchPass: function(){
            return this.urlParams.edit === EnumEditPage.OneTouchPass;
        },
        isEmailSetting: function(){
            return this.urlParams.edit === EnumEditPage.EmailSetting;
        },
        // FIXME: prop で受け取る thymeleaf の値が v-bind で渡せない?ため、文字列から boolean に変換する
        isChanged: function(){
            return this.dataChanged === 'true';
        },
        isPasswordPage: function(){
            return this.dataPasswordPage === 'true';
        },
        hasError: function(){
            // エラーメッセージがあれば slot 内に表示される
            const errorMessageNodes = document.querySelectorAll('.msg .errNote');

            return errorMessageNodes.length !== 0
        },
        /**
         * 表示している画面のURLから必要なクエリストリング情報を返す
         * @returns {{edit: string, club_cd: null}}
         */
        urlParams: function(){
            let _params = {
                edit: this.dataParamEdit,
                club_cd: null,
            };

            // 完了画面やエラー画面のとき、クエリストリング情報がとれない
            // リファラからクエリストリング情報を取得してパラメータに追加する
            if (this.dataParamEdit === '') {
                // 更新完了またはエラーメッセージが表示されている (edit パラメーターなし)
                // edit を設定する
                _params.edit = this.getUrlParamByKey(this.referrer, EDIT_USERINFO_PARAM_KEY);

                // 直接アクセスまたは定義されていない edit パラメーターのときはトップページにリダイレクトする
                const result = Object.keys(EnumEditPage).some(function(key){
                    return EnumEditPage[key] === _params.edit;
                });
                if (!result) this.backToHome();

                if (this.hasError) {
                    // エラーメッセージが表示されている ∧ メール設定のとき
                    // クラブコードを設定する
                    if (this.isEmailSetting) _params.club_cd = this.getUrlParamByKey(this.referrer, CLUB_CODE_PARAM_KEY);
                }
            }
            return _params;
        },
    },
    mounted: function(){
        const client = this.jleagueClient;
        if (Object.keys(client).length !== 0 && client.hasOwnProperty('redirect_url')) {
            const backToServiceButtons = document.getElementsByClassName('js-back-to-service');
            Array.prototype.forEach.call(backToServiceButtons, function(element){
                element.setAttribute('href', client.redirect_url);
            });
        }
        // サービスに戻るボタンにsidをセットする
        if(document.getElementById('backButton')){
            var target = document.getElementById('backButton');
            var query = getParameter(location.href);
            if (this.isOneTouchPass) {
                query['sid'] = '3005';
                target.href = setParameter(query, target.href);
            } else {
                query['sid'] = '3001';
                target.href = setParameter(query, target.href);
            }
        }
        if (this.isOneTouchPass && this.isChanged) {

            this.showLoading = true;
            setTimeout(() => {
                this.showLoading = false;
            }, 2000);
        }
    },
    created: function() {
    },
    methods: {
        alertMessage: function(message, type){
            this.$toasted.show(message, {
                icon: this.alertIcon,
                type: type
            });
        },
        addOneTouchPass: function(isAddOneTouchPass){
            this.isAddOneTouchPass = isAddOneTouchPass;
        },
    },
}
</script>
